import React, { useEffect, useState } from "react";
import axios from "axios";
import "../../styling/Analytics.css";

const AnalyticsPanel = () => {
  const [ytdRevenue, setYtdRevenue] = useState("$0");
  const [ytdGrowth, setYtdGrowth] = useState(0);
  const [newClients, setNewClients] = useState(0);
  const [clientsGrowth, setClientsGrowth] = useState(0);
  const [newBookings, setNewBookings] = useState(0);
  const [bookingsGrowth, setBookingsGrowth] = useState(0);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const formatAmount = (amount) => {
    if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
    if (amount >= 1_000) return `$${(amount / 1_000).toFixed(1)}k`;
    return `$${amount}`;
  };

  const calcGrowth = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return (((current - previous) / previous) * 100).toFixed(0);
  };

  // NEW: Helper function to determine the color of the percentage
  const getGrowthColor = (growth) => {
    return parseFloat(growth) < 0 ? '#E02121' : '#21A62C'; // Red if negative, Green if positive/0
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/business/events`,
        { withCredentials: true }
      );

      const events = response.data.data;
      const now = new Date();
      const currentYear = now.getFullYear();
      const lastYear = currentYear - 1;
      const currentMonth = now.getMonth();
      const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const currentMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

      let currentYtdSum = 0, lastYtdSum = 0;
      const currentMonthClients = new Set();
      const lastMonthClients = new Set();
      let currentBookings = 0, lastBookings = 0;

      events.forEach(event => {
        const eventDate = new Date(event.createdAt);
        const eventYear = eventDate.getFullYear();
        const eventMonth = eventDate.getMonth();

        // YTD Revenue
        if (eventYear === currentYear) {
          currentYtdSum += event.expPaymentAmount || 0;
        } else if (eventYear === lastYear) {
          lastYtdSum += event.expPaymentAmount || 0;
        }

        // Clients
        if (eventMonth === currentMonth && eventYear === currentYear) {
          currentMonthClients.add(event.userId);
          currentBookings++;
        } else if (eventMonth === lastMonth && eventYear === currentMonthYear) {
          lastMonthClients.add(event.userId);
          lastBookings++;
        }
      });

      setYtdRevenue(formatAmount(currentYtdSum));
      setYtdGrowth(calcGrowth(currentYtdSum, lastYtdSum));
      setNewBookings(currentBookings);
      setBookingsGrowth(calcGrowth(currentBookings, lastBookings));

      // New Clients = currentMonthClients not in lastMonthClients
      const newUniqueClients = [...currentMonthClients].filter(
        id => !lastMonthClients.has(id)
      );

      const numNewClients = newUniqueClients.length;
      const lastMonthNewClients = [...lastMonthClients].filter(
        id => !currentMonthClients.has(id)
      ).length;

      setNewClients(numNewClients);
      setClientsGrowth(calcGrowth(numNewClients, lastMonthNewClients));

    } catch (error) {
      console.error("Error fetching analytics data:", error);
    }
  };

  return (
    <div className="right-panel-analytics" style={{paddingBottom: '20px'}}>
      <div>
        <h2
          style={{
            paddingTop: "20px",
            paddingLeft: "15px",
            fontWeight: "500",
            color: "#1C1154",
          }}
        >
          Sales Summary
        </h2>

        {/* 1. YTD BLOCK */}
        <div className="right-panel-content-analytics">
          <div className="right-panel-arrow-container" style={{ display: 'flex', justifyContent: 'space-between' }}>
            
            <div>
              <h4 style={{ color: "#1C1154", marginTop: "0px", marginBottom: "5px", paddingTop: "0px",  fontWeight: "bold",  }}>
                YTD
              </h4>
              <div style={{ marginBottom: "15px" }}>Year to date</div>
            </div>
            
            {/* UPDATED: Dynamic Color and 'top: -5px' alignment */}
            <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '5px' }}>
              <div style={{ color: "#1C1154", fontSize: "36px", fontWeight: '400', lineHeight: '1' }}>
                {ytdRevenue}
              </div>
              <div style={{ color: getGrowthColor(ytdGrowth), fontSize: '15px', fontWeight: '500', position: 'relative', top: '-10px' }}>
                {ytdGrowth}%
              </div>
            </div>

          </div>
        </div>

        {/* 2. NEW CLIENTS BLOCK */}
        <div className="right-panel-content-analytics">
          <div className="right-panel-arrow-container" style={{ display: 'flex', justifyContent: 'space-between' }}>
            
            <div>
              <h4 style={{ color: "#1C1154", marginTop: "0px", marginBottom: "5px", paddingTop: "0px", fontWeight: 'bold' }}>
                New Clients
              </h4>
              <div style={{ marginBottom: "15px" }}>Unique from last month</div>
            </div>
            
            {/* UPDATED: Dynamic Color and 'top: -5px' alignment */}
            <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '5px' }}>
              <div style={{ color: "#1C1154", fontWeight: "400", fontSize: "36px", lineHeight: '1' }}>
                {newClients}
              </div>
              <div style={{ color: getGrowthColor(clientsGrowth), fontSize: '15px',fontWeight: '500', position: 'relative', top: '-10px' }}>
                {clientsGrowth}%
              </div>
            </div>

          </div>
        </div>

        {/* 3. NEW BOOKINGS BLOCK */}
        <div className="right-panel-content-analytics" style={{borderBottom: "none"}}>
          <div className="right-panel-arrow-container" style={{ display: 'flex', justifyContent: 'space-between' }}>
            
            <div>
              <h4 style={{ color: "#1C1154", marginTop: "0px", marginBottom: "5px", paddingTop: "0px", fontWeight: 'bold' }}>
                New Bookings
              </h4>
              <div style={{ marginBottom: "15px" }}>For the last 30 days</div>
            </div>
            
            {/* UPDATED: Dynamic Color and 'top: -5px' alignment */}
            <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '5px' }}>
              <div style={{ color: "#1C1154", fontWeight: "400", fontSize: "36px", lineHeight: '1' }}>
                {newBookings}
              </div>
              <div style={{ color: getGrowthColor(bookingsGrowth), fontSize: '15px',  fontWeight: '500', position: 'relative', top: '-10px' }}>
                {bookingsGrowth}%
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default AnalyticsPanel;